/**
 * RitualProgressNav Component
 *
 * Left-side navigation indicator for pre-trading ritual phases.
 * - Desktop: Dots expand on hover/focus to show labels, click to navigate
 * - Mobile: Passive position indicator only (no interaction)
 *
 * Uses IntersectionObserver in parent to track current section.
 */

// 1. External dependencies
import React from 'react';

/**
 * Section definition for navigation
 */
export interface RitualSection {
  /** Unique section identifier (matches data-section-id) */
  id: string;
  /** Display label for the section */
  label: string;
  /** Optional icon element */
  icon?: React.ReactNode;
}

/**
 * Props for RitualProgressNav
 */
export interface RitualProgressNavProps {
  /** Array of section definitions */
  sections: RitualSection[];
  /** Currently active section ID (from IntersectionObserver) */
  currentSectionId: string;
  /** Callback when user clicks a section to navigate (desktop only) */
  onNavigate: (sectionId: string) => void;
}

/**
 * RitualProgressNav - Left-side progress navigation
 *
 * Features:
 * - Fixed position on left side, vertically centered
 * - Dots indicate sections, current section highlighted
 * - Desktop: hover/focus expands to show labels, click navigates
 * - Mobile: dots only, no interaction (scroll is primary nav)
 * - Accessible: semantic nav, aria-labels, keyboard support
 */
export const RitualProgressNav: React.FC<RitualProgressNavProps> = ({
  sections,
  currentSectionId,
  onNavigate,
}) => {
  return (
    <>
      {/* Desktop: Interactive nav with hover/focus expansion */}
      <nav
        aria-label="Ritual progress"
        className="
          fixed left-3 top-1/2 -translate-y-1/2 z-50
          hidden md:flex flex-col items-start gap-3
          group
        "
      >
        {sections.map((section) => {
          const isCurrent = section.id === currentSectionId;

          return (
            <button
              key={section.id}
              type="button"
              onClick={() => onNavigate(section.id)}
              aria-label={section.label}
              aria-current={isCurrent ? 'step' : undefined}
              className="
                flex items-center gap-2
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
                rounded-full
                transition-all duration-200
              "
            >
              {/* Dot indicator */}
              <span
                className={`
                  flex-shrink-0 rounded-full transition-all duration-200
                  ${isCurrent
                    ? 'w-2.5 h-2.5 bg-primary-500 shadow-sm shadow-primary-500/50'
                    : 'w-2 h-2 bg-default-400 hover:bg-default-500'
                  }
                `}
              />

              {/* Label - hidden by default, shown on group hover/focus */}
              <span
                className={`
                  text-sm whitespace-nowrap
                  opacity-0 max-w-0 overflow-hidden
                  group-hover:opacity-100 group-hover:max-w-xs
                  group-focus-within:opacity-100 group-focus-within:max-w-xs
                  transition-all duration-200
                  ${isCurrent ? 'text-primary-600 font-medium' : 'text-text-secondary'}
                `}
              >
                {section.label}
              </span>
            </button>
          );
        })}
      </nav>

      {/* Mobile: Passive position indicator (no interaction) */}
      <div
        aria-hidden="true"
        className="
          fixed left-2 top-1/2 -translate-y-1/2 z-50
          flex md:hidden flex-col gap-2
        "
      >
        {sections.map((section) => {
          const isCurrent = section.id === currentSectionId;
          return (
            <span
              key={section.id}
              className={`
                rounded-full transition-all duration-200
                ${isCurrent
                  ? 'w-1.5 h-1.5 bg-primary-500'
                  : 'w-1 h-1 bg-default-400'
                }
              `}
            />
          );
        })}
      </div>
    </>
  );
};
